import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Drawer, InlineError, Input, Skeleton } from '../../components/ui';
import { ByAd, ScoreboardOverview } from './ScoreboardOverview';
import { SectionNumbers, Source, type DaysReader } from './SectionNumbers';
import { entryCounts, loadOpenSections, saveOpenSections } from './scoreboardDashboard';
import { ScoreEntryLinks } from './ScoreEntryLinks';
import { groupItems, itemTableNames, splitItemLabel } from './scoreboardDetails';
import { FieldListEditor } from './FieldListEditor';
import type { ScoreApi, ScoreField, ScoreLoad, ScoreSection } from './scoreboard';
import {
  editableField,
  isWeekly,
  lastEntryDay,
  plainLabel,
  rawFields,
  scoreError,
  validateFields,
  validEntryDay,
  valuesFromDraft,
} from './scoreboard';
import './scoreboard.css';

type Editor = { section: ScoreSection; mode: 'entry' | 'fields' };

export function ScoreboardScreen({
  api,
  cacheScope = 'local-qa',
  days,
  aboveTiles = null,
  toolsHost = null,
}: {
  api: ScoreApi;
  cacheScope?: string;
  /** Raw days for the open Details cards. Optional so the screen still works without it. */
  days?: DaysReader;
  /** v3: the Analyze button, shown with the day controls. */
  aboveTiles?: ReactNode;
  /** Where the day controls and Analyze go: the tab bar's right side. Inline when absent. */
  toolsHost?: HTMLElement | null;
}) {
  const [date, setDate] = useState(lastEntryDay);
  const [editor, setEditor] = useState<Editor | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const openEditor = (section: ScoreSection, mode: Editor['mode']) => {
    if (editor) return;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setEditor({ section, mode });
  };
  useEffect(() => {
    if (!editor) opener.current?.focus();
  }, [editor]);
  const [notice, setNotice] = useState('');
  const [openSections, setOpenSections] = useState(() => loadOpenSections(cacheScope));
  const max = lastEntryDay();
  const validDate = validEntryDay(date, max);
  const board = useQuery({
    queryKey: ['marketing-scoreboard', cacheScope, date],
    queryFn: () => api.totals(date),
    enabled: validDate,
    retry: false,
  });

  const controls = (
    <>
      <Input
        label="Selected day (Manila)"
        labelHidden
        type="date"
        value={date}
        max={max}
        required
        disabled={editor !== null}
        onChange={(event) => {
          setDate(event.target.value);
          setNotice('');
        }}
      />
      <Button variant="secondary" disabled={editor !== null} onClick={() => setDate(max)}>
        Yesterday
      </Button>
      <Button
        variant="ghost"
        disabled={board.isFetching || !validDate}
        onClick={() => void board.refetch()}
      >
        Refresh
      </Button>
      {aboveTiles}
    </>
  );

  return (
    <div className="scoreboard">
      {toolsHost ? (
        createPortal(<div className="score-tools">{controls}</div>, toolsHost)
      ) : (
        <div className="score-tools">{controls}</div>
      )}
      {!validDate ? <InlineError>Choose yesterday or an earlier Manila date.</InlineError> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {validDate && board.isPending ? (
        <Skeleton lines={8} label="Loading Marketing Scoreboard" />
      ) : null}
      {board.isError ? <InlineError>{scoreError(board.error)}</InlineError> : null}
      {validDate && !board.isError && board.data?.length === 0 ? (
        <p>No sections have been set up yet.</p>
      ) : null}
      {validDate && !board.isError && board.data?.length ? (
        <ScoreboardOverview
          sections={board.data}
          yesterday={date === max}
          onEntry={(section) => {
            openEditor(section, 'entry');
          }}
        />
      ) : null}
      <h2>Itemized numbers</h2>
      {validDate && !board.isError
        ? board.data?.map((section) => (
            <section
              className={`score-card score-detail-card${openSections.includes(section.id) ? ' is-open' : ''}`}
              key={section.id}
              aria-labelledby={`score-${section.id}`}
            >
              <header className="score-card-head">
                <div>
                  <h3 className="score-section-title" id={`score-${section.id}`}>
                    <Button
                      variant="ghost"
                      aria-expanded={openSections.includes(section.id)}
                      aria-controls={`details-${section.id}`}
                      onClick={() => {
                        const next = openSections.includes(section.id)
                          ? openSections.filter((id) => id !== section.id)
                          : [...openSections, section.id];
                        setOpenSections(next);
                        saveOpenSections(cacheScope, next);
                      }}
                    >
                      <span className="score-chevron" aria-hidden="true">
                        {openSections.includes(section.id) ? '▾' : '▸'}
                      </span>
                      {section.name}
                    </Button>
                  </h3>
                  {openSections.includes(section.id) ? (
                    <p className="score-muted score-meta">
                      {section.is_open ? 'Open to TAP' : (section.owner_name ?? 'Unassigned')} ·{' '}
                      {entryCounts(section).entered} of {entryCounts(section).total}{' '}
                      {entryCounts(section).total !== section.total_fields ? 'daily ' : ''}fields ·{' '}
                      {section.entered_at
                        ? `saved ${new Date(section.entered_at).toLocaleString('en-US', {
                            timeZone: 'Asia/Manila',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })} Manila`
                        : 'not saved for this day'}
                    </p>
                  ) : null}
                </div>
                {section.can_edit ? (
                  <div className="score-actions">
                    <Button
                      variant="secondary"
                      disabled={editor !== null}
                      onClick={() => openEditor(section, 'entry')}
                    >
                      Enter numbers
                      <span className="ui-visually-hidden">: {section.name} details</span>
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={editor !== null}
                      onClick={() => openEditor(section, 'fields')}
                    >
                      Edit fields<span className="ui-visually-hidden">: {section.name}</span>
                    </Button>
                  </div>
                ) : (
                  <small className="score-muted">Read only</small>
                )}
              </header>
              <div id={`details-${section.id}`} hidden={!openSections.includes(section.id)}>
                {openSections.includes(section.id) ? (
                  <>
                    <SectionNumbers
                      section={section}
                      date={date}
                      yesterday={date === max}
                      cacheScope={cacheScope}
                      {...(days ? { days } : {})}
                    />
                    {section.metrics.length === 0 ? (
                      <p className="score-empty">No visible fields.</p>
                    ) : null}
                    {section.slug === 'linkedin-ads' ? <ByAd section={section} /> : null}
                  </>
                ) : null}
              </div>
            </section>
          ))
        : null}
      {editor && board.data?.find((s) => s.id === editor.section.id)?.can_edit ? (
        <EntryLoader
          key={`${editor.section.id}:${editor.mode}:${date}`}
          api={api}
          cacheScope={cacheScope}
          editor={editor}
          date={date}
          onBack={() => setEditor(null)}
          onSaved={(message) => {
            setEditor(null);
            setNotice(message);
            void board.refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function EntryLoader({
  api,
  cacheScope,
  editor,
  date,
  onBack,
  onSaved,
}: {
  api: ScoreApi;
  cacheScope: string;
  editor: Editor;
  date: string;
  onBack: () => void;
  onSaved: (message: string) => void;
}) {
  const [revision, setRevision] = useState(0);
  const close = useRef(onBack);
  // Refetching a snapshot behind a dirty form would replace its conflict tokens.
  // No focus/reconnect polling; reload is an explicit discard-and-reconcile action.
  const load = useQuery({
    queryKey: ['marketing-score-entry', cacheScope, editor.section.id, date, revision],
    queryFn: () => api.load(editor.section.id, date),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  return (
    <Drawer
      open
      title={`${editor.mode === 'entry' ? 'Daily entry' : 'Edit fields'} · ${editor.section.name}`}
      onClose={() => close.current()}
    >
      <div className="scoreboard">
        {load.isPending ? (
          <Skeleton label="Loading entry snapshot" lines={6} />
        ) : load.isError ? (
          <>
            <InlineError>{scoreError(load.error)}</InlineError>
            <Button onClick={() => setRevision((n) => n + 1)}>Retry load</Button>
            <Button variant="ghost" onClick={onBack}>
              Back to Board
            </Button>
          </>
        ) : (
          <ScoreEditor
            key={revision}
            api={api}
            editor={editor}
            snapshot={load.data}
            onBack={onBack}
            onSaved={onSaved}
            onReload={() => setRevision((n) => n + 1)}
            closeRef={close}
          />
        )}
      </div>
    </Drawer>
  );
}

function ScoreEditor({
  api,
  editor,
  snapshot: loaded,
  onBack,
  onSaved,
  onReload,
  closeRef,
}: {
  api: ScoreApi;
  editor: Editor;
  snapshot: ScoreLoad;
  onBack: () => void;
  onSaved: (message: string) => void;
  onReload: () => void;
  closeRef: React.RefObject<() => void>;
}) {
  // Freeze values AND both versions together, even if the query cache changes.
  const [snapshot] = useState(loaded);
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      snapshot.metrics.map((field) => [field.id, field.value === null ? '' : String(field.value)]),
    ),
  );
  const [fields, setFields] = useState(() => snapshot.metrics.map(editableField));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [addType, setAddType] = useState<ScoreField['value_type']>('count');
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    formRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, []);
  useEffect(() => {
    closeRef.current = () => {
      if (!busy && (!dirty || window.confirm('Discard your unsaved draft?'))) onBack();
    };
  }, [busy, dirty, closeRef, onBack]);
  useEffect(() => {
    if (!dirty && !busy) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, busy]);
  const leave = (action: () => void) => {
    if (!dirty || window.confirm('Discard your unsaved draft?')) action();
  };
  const changeField = (id: string, change: Partial<ScoreField>) => {
    setDirty(true);
    setFields((current) => current.map((f) => (f.id === id ? { ...f, ...change } : f)));
  };
  const move = (from: number, to: number) => {
    if (to < 0 || to >= fields.length || from === to) return;
    const updated = [...fields];
    const [picked] = updated.splice(from, 1);
    if (!picked) return;
    updated.splice(to, 0, picked);
    setFields(updated.map((f, position) => ({ ...f, position })));
    setDirty(true);
  };
  const save = async () => {
    setError('');
    setBusy(true);
    try {
      if (editor.mode === 'entry')
        await api.save(snapshot, valuesFromDraft(snapshot.metrics, draft));
      else {
        validateFields(fields, editor.section.kind);
        await api.editFields(snapshot, fields);
      }
      setDirty(false);
      onSaved(
        editor.mode === 'entry'
          ? `${editor.section.name}: day saved.`
          : `${editor.section.name}: fields saved.`,
      );
    } catch (cause) {
      setError(scoreError(cause));
      if (typeof cause === 'object' && cause !== null && 'code' in cause && cause.code === '40001')
        setConflict(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {editor.mode === 'entry' ? (
        <p className="score-muted score-entry-note">
          {new Date(`${snapshot.entry_date}T00:00:00Z`).toLocaleDateString('en-US', {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
          })}{' '}
          · Manila. Leave unknown blank; 0 only for a known zero.
        </p>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      {conflict ? (
        <Button variant="secondary" onClick={() => leave(onReload)}>
          Reload saved version
        </Button>
      ) : null}
      <fieldset disabled={busy} className="score-editor-fields">
        <legend className="ui-visually-hidden">
          {editor.mode === 'entry' ? 'Daily values' : 'Section fields'}
        </legend>
        {editor.mode === 'entry' ? (
          <EntryFields
            fields={rawFields(snapshot.metrics)}
            draft={draft}
            onChange={(id, text) => {
              setDirty(true);
              setDraft({ ...draft, [id]: text });
            }}
          />
        ) : (
          <FieldListEditor
            fields={fields}
            kind={editor.section.kind}
            addType={addType}
            onAddType={setAddType}
            onChange={changeField}
            onMove={move}
            onDelete={(id) => {
              setDirty(true);
              setFields((current) => current.filter((f) => f.id !== id));
            }}
            onAdd={() => {
              const id = crypto.randomUUID();
              setDirty(true);
              setFields([
                ...fields,
                {
                  id,
                  label: '',
                  value_type: addType,
                  numerator_id: null,
                  denominator_id: null,
                  rate_format: addType === 'rate' ? 'percent' : null,
                  source_url: null,
                  position: Math.max(0, ...fields.map((f) => f.position)) + 1,
                  target: null,
                  is_active: true,
                },
              ]);
              return id;
            }}
          />
        )}
      </fieldset>
      {editor.mode === 'fields' &&
      editor.section.slug === 'linkedin-ads' &&
      editor.section.is_manager ? (
        <ScoreEntryLinks api={api} section={editor.section.id} />
      ) : null}
      <div className="score-save-bar">
        <Button
          type="submit"
          disabled={
            busy ||
            conflict ||
            (editor.mode === 'entry' && rawFields(snapshot.metrics).length === 0)
          }
        >
          {busy ? 'Saving…' : editor.mode === 'entry' ? 'Save day' : 'Save fields'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => leave(onBack)}>
          Cancel
        </Button>
        <span role="status" className="score-muted">
          {dirty ? 'Unsaved draft' : 'No changes yet'}
        </span>
      </div>
    </form>
  );
}

/**
 * Daily entry. Plain fields as a list; "Item · Metric" fields as a grid, one row per item, so
 * eight videos stay one compact table. "(weekly)" fields wait behind one switch.
 */
function EntryFields({
  fields,
  draft,
  onChange,
}: {
  fields: ScoreField[];
  draft: Record<string, string>;
  onChange: (id: string, text: string) => void;
}) {
  const [weekly, setWeekly] = useState(false);
  const hasWeekly = fields.some((f) => isWeekly(f.label));
  const shown = (f: ScoreField) => weekly || !isWeekly(f.label);
  const plain = fields.filter((f) => !splitItemLabel(f.label) && shown(f));
  const tables = groupItems(fields.filter((f) => splitItemLabel(f.label)));
  const input = (field: ScoreField, label: string, hidden: boolean) => (
    <Input
      label={label}
      labelHidden={hidden}
      type="number"
      min="0"
      step={field.value_type === 'count' ? '1' : 'any'}
      inputMode={field.value_type === 'count' ? 'numeric' : 'decimal'}
      value={draft[field.id] ?? ''}
      onChange={(event) => onChange(field.id, event.target.value)}
    />
  );
  if (!fields.length)
    return (
      <section className="score-card score-entry-list">
        <p>No active raw fields. Add fields first.</p>
      </section>
    );
  return (
    <>
      {hasWeekly ? (
        <Checkbox
          label="Show weekly fields"
          checked={weekly}
          onChange={(event) => setWeekly(event.target.checked)}
        />
      ) : null}
      {plain.length ? (
        <section className="score-card score-entry-list">
          {plain.map((field) => (
            <div className="score-entry-row" key={field.id}>
              {input(
                field,
                `${plainLabel(field.label)}${field.value_type === 'money' ? ' (USD)' : ''}`,
                false,
              )}
              <span className="score-entry-source">
                <Source url={field.source_url} />
              </span>
            </div>
          ))}
        </section>
      ) : null}
      {tables.map((table) => {
        const columns = table.columns.filter((c) => weekly || !c.weekly);
        if (!columns.length) return null;
        return (
          <div
            key={table.columns.map((c) => c.name).join('|')}
            className="score-table-scroll score-card score-entry-grid"
            role="region"
            aria-label="Numbers by item"
            tabIndex={0}
          >
            <table className="score-table">
              <thead>
                <tr>
                  <th scope="col">{itemTableNames(table.columns).head}</th>
                  {columns.map((column) => (
                    <th scope="col" key={column.name}>
                      {column.name}
                      {column.weekly ? <em className="sn-tag">weekly</em> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr key={row.name}>
                    <th scope="row">{row.name}</th>
                    {columns.map((column) => {
                      const cell = row.cells[column.name];
                      return (
                        <td key={column.name}>
                          {cell ? (
                            input(cell.field, `${row.name} · ${column.name}`, true)
                          ) : (
                            <span className="score-muted">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}
